import {GitHubAvatar} from '@github-ui/github-avatar'
import {reportAbusePath} from '@github-ui/paths'
import {LinkExternalIcon, LinkIcon} from '@primer/octicons-react'
import {Button, Dialog, Link} from '@primer/react'
import {SponsorHeartIcon} from './SponsorHeartIcon'

import type {SponsorableUser, FundingLink} from '../types'
import {getPlatformIconPath, formatExternalLink} from '../utils/platform'
import styles from '../SponsorButton.module.css'
import {useMemo} from 'react'

const SPONSORS_LEARN_MORE_URL = '/sponsors'
const FUNDING_DOCS_URL = '/articles/displaying-a-sponsor-button-in-your-repository'
function isSafeUrl(url: string): boolean {
  try {
    const parsed = new URL(url.trim(), window.location.origin)
    return parsed.protocol === 'http:' || parsed.protocol === 'https:'
  } catch {
    return false
  }
}

function GitHubSponsorCard({displayMode, sponsorable}: {displayMode: 'grid' | 'row'; sponsorable: SponsorableUser}) {
  const buttonText = sponsorable.isSponsoredByViewer ? 'Sponsoring' : 'Sponsor'
  const sponsorUrl = `/sponsors/${sponsorable.login}`
  const showSponsorButton = sponsorable.viewerCanSponsor !== false

  return (
    <div
      className={`Box color-shadow-small color-bg-muted border rounded-2 tmp-p-3 d-flex ${displayMode === 'grid' ? 'flex-column flex-justify-between' : 'flex-column flex-md-row flex-md-items-center flex-md-justify-between'}`}
      data-testid="github-sponsor-card"
    >
      <div className="d-flex flex-items-center min-width-0">
        {sponsorable.profilePath && sponsorable.avatarUrl ? (
          <Link href={sponsorable.profilePath} className="color-fg-default no-underline mr-2 flex-shrink-0" muted>
            <GitHubAvatar
              src={sponsorable.avatarUrl}
              square={sponsorable.isOrg}
              size={40}
              alt={`@${sponsorable.login}`}
            />
          </Link>
        ) : null}
        <div className="min-width-0">
          {sponsorable.profilePath ? (
            <Link href={sponsorable.profilePath} className="color-fg-default no-underline h4 mb-0 d-block" muted>
              {sponsorable.displayName ?? sponsorable.login}
            </Link>
          ) : (
            <p className="h4 mb-0">{sponsorable.displayName ?? sponsorable.login}</p>
          )}
          {sponsorable.profilePath ? (
            <Link href={sponsorable.profilePath} className="color-fg-muted no-underline f4 d-block" muted>
              {sponsorable.login}
            </Link>
          ) : (
            <p className="color-fg-muted f4 mb-0">{sponsorable.login}</p>
          )}
        </div>
      </div>
      {showSponsorButton && (
        <Button
          as="a"
          href={sponsorUrl}
          size="small"
          block={displayMode === 'grid'}
          className={displayMode === 'grid' ? 'tmp-mt-3' : 'tmp-mt-3 tmp-mt-md-0'}
          leadingVisual={() => (
            <SponsorHeartIcon
              isSponsoring={sponsorable.isSponsoredByViewer}
              suppressAnimation={sponsorable.isViewerSponsorable}
            />
          )}
        >
          {buttonText}
        </Button>
      )}
    </div>
  )
}

function ExternalLinkText({label, url}: {label?: string; url: string}) {
  if (label) return <>{label}</>
  const formatted = formatExternalLink(url)
  return (
    <>
      {formatted.prefix}
      {formatted.emphasized ? <strong>{formatted.emphasized}</strong> : null}
    </>
  )
}

export interface SponsorDialogProps {
  repoNameWithOwner: string
  sponsorables: SponsorableUser[]
  externalFundingLinks: FundingLink[]
  onClose: () => void
  returnFocusRef: React.RefObject<HTMLButtonElement | null>
  githubSectionTitleId: string
  externalSectionTitleId: string
}

export function SponsorDialog({
  repoNameWithOwner,
  sponsorables,
  externalFundingLinks,
  onClose,
  returnFocusRef,
  githubSectionTitleId,
  externalSectionTitleId,
}: SponsorDialogProps) {
  const reportAbuseUrl = reportAbusePath({report: `${repoNameWithOwner} (Repository Funding Links)`})
  // Render org sponsorables before individual users (matches Rails behavior)
  const sortedSponsorables = useMemo(
    () => [...sponsorables].sort((a, b) => (b.isOrg ? 1 : 0) - (a.isOrg ? 1 : 0)),
    [sponsorables],
  )
  const safeExternalLinks = useMemo(
    () => externalFundingLinks.filter(link => isSafeUrl(link.url)),
    [externalFundingLinks],
  )

  return (
    <Dialog title={`Sponsor ${repoNameWithOwner}`} onClose={onClose} returnFocusRef={returnFocusRef} width="xlarge">
      <Dialog.Body>
        <div className="d-flex flex-column gap-4">
          {sortedSponsorables.length > 0 ? (
            <div role="group" aria-labelledby={githubSectionTitleId}>
              <div className="d-flex flex-items-center flex-justify-between gap-3 tmp-mb-3">
                <h2 id={githubSectionTitleId} className="h3 mb-0">
                  GitHub Sponsors
                </h2>
                <Link href={SPONSORS_LEARN_MORE_URL}>Learn more about GitHub Sponsors</Link>
              </div>
              <div className={sortedSponsorables.length > 1 ? 'gutter-condensed d-flex flex-wrap' : undefined}>
                {sortedSponsorables.map(sponsorable => {
                  const isGrid = sortedSponsorables.length > 1

                  return (
                    <div key={sponsorable.login} className={isGrid ? 'col-12 col-md-6 tmp-mb-3' : undefined}>
                      <GitHubSponsorCard sponsorable={sponsorable} displayMode={isGrid ? 'grid' : 'row'} />
                    </div>
                  )
                })}
              </div>
            </div>
          ) : null}

          {safeExternalLinks.length > 0 ? (
            <div role="group" aria-labelledby={externalSectionTitleId}>
              <h2 id={externalSectionTitleId} className="h3 tmp-mb-3">
                External links
              </h2>
              <div>
                {safeExternalLinks.map(link => {
                  const iconSrc = link.iconUrl ?? getPlatformIconPath(link.platform)

                  return (
                    <Link
                      key={`${link.platform}-${link.url}`}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="d-flex flex-items-center color-fg-accent no-underline tmp-mb-3"
                      data-testid="funding-link-item"
                    >
                      <span
                        className={`circle mr-2 border d-flex flex-justify-center flex-items-center flex-shrink-0 color-fg-muted ${styles.platformIconCircle}`}
                        aria-hidden="true"
                      >
                        {iconSrc ? <img src={iconSrc} alt="" className={styles.platformIconImage} /> : <LinkIcon />}
                      </span>
                      <span className="flex-auto min-width-0">
                        <ExternalLinkText label={link.label} url={link.url} />
                      </span>
                      <LinkExternalIcon className="ml-2 flex-shrink-0" />
                    </Link>
                  )
                })}
              </div>
            </div>
          ) : null}

          <div className="text-small border-top tmp-pt-3">
            <p className="mb-1">
              <Link inline href={FUNDING_DOCS_URL}>
                Learn more about funding links in repositories
              </Link>
              .
            </p>
            <p className="mb-0">
              <Link muted href={reportAbuseUrl}>
                Report abuse
              </Link>
            </p>
          </div>
        </div>
      </Dialog.Body>
    </Dialog>
  )
}
